import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import { updateConsent } from '../utils/gtm';
import '../styles/CookieConsentBanner.scss';

const CookieConsentBanner = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consentSettings, setConsentSettings] = useState({
    analytics: false,
    advertising: false
  });

  useEffect(() => {
    // Перевіряємо, чи користувач вже надавав згоду
    const consentAccepted = localStorage.getItem('cookieConsentAccepted');
    
    // Якщо згода вже була надана, відновлюємо налаштування для GTM
    if (consentAccepted) {
      const savedSettings = localStorage.getItem('cookieConsentSettings');
      if (savedSettings) {
        try {
          const parsedSettings = JSON.parse(savedSettings);
          setConsentSettings(parsedSettings);
          
          // Оновлюємо згоду згідно збережених налаштувань
          if (parsedSettings.analytics && parsedSettings.advertising) {
            updateConsent('all');
          } else if (parsedSettings.analytics) {
            updateConsent('analytics');
          } else if (parsedSettings.advertising) {
            updateConsent('ads');
          } else {
            updateConsent('none');
          }
        } catch (e) {
          console.error('Помилка при обробці збережених налаштувань згоди:', e);
        }
      }
    }
    // Показуємо банер, якщо згода ще не була надана
    else {
      // Невелика затримка перед показом банера для кращого UX
      const timer = setTimeout(() => {
        setVisible(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    // Зберігаємо згоду в localStorage
    localStorage.setItem('cookieConsentAccepted', 'true');
    // Зберігаємо дату надання згоди
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    
    // Налаштування згоди на всі типи обробки даних
    const settings = { analytics: true, advertising: true };
    setConsentSettings(settings);
    localStorage.setItem('cookieConsentSettings', JSON.stringify(settings));
    
    // Оновлюємо згоду для Google Tag Manager
    updateConsent('all');
    
    setVisible(false);
  };

  const handleReject = () => {
    // Зберігаємо відмову в localStorage
    localStorage.setItem('cookieConsentAccepted', 'false');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    
    // Налаштування відмови від усіх типів обробки даних
    const settings = { analytics: false, advertising: false };
    setConsentSettings(settings);
    localStorage.setItem('cookieConsentSettings', JSON.stringify(settings));
    
    // Оновлюємо згоду для Google Tag Manager
    updateConsent('none');
    
    setVisible(false);
  };

  const handleCustomize = () => {
    // Відкриваємо налаштування
    setShowSettings(true);
  };

  const openPrivacyPolicy = (e) => {
    e.preventDefault();
    setShowPrivacyPolicy(true);
  };

  const handleSettingsChange = (type) => {
    setConsentSettings(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const saveSettings = () => {
    // Зберігаємо налаштування в localStorage
    localStorage.setItem('cookieConsentAccepted', 'true');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    localStorage.setItem('cookieConsentSettings', JSON.stringify(consentSettings));
    
    // Оновлюємо згоду для Google Tag Manager на основі налаштувань
    if (consentSettings.analytics && consentSettings.advertising) {
      updateConsent('all');
    } else if (consentSettings.analytics) {
      updateConsent('analytics');
    } else if (consentSettings.advertising) {
      updateConsent('ads');
    } else {
      updateConsent('none');
    }
    
    setShowSettings(false);
    setVisible(false);
  };

  if (!visible) {
    return null;
  }

  return (
    <>
      <div className="cookie-consent-banner">
        <div className="cookie-consent-content">
          {!showSettings ? (
            // Основний вміст банера
            <>
              <div className="cookie-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-4h2v2h-2v-2zm0-10h2v8h-2V6z" />
                </svg>
              </div>
              <div className="cookie-text">
                <h3>{t('cookies.title', 'Ми цінуємо вашу приватність')}</h3>
                <p>
                  {t('cookies.description', 'Ми використовуємо необхідні cookies, щоб забезпечити роботу нашого сайту. За вашої згоди ми також можемо використовувати аналітичні файли cookie для покращення вашого досвіду та вимірювання ефективності нашого сайту. Натискаючи "Прийняти все", ви погоджуєтесь з використанням усіх файлів cookie. Ви можете налаштувати свої вподобання, натиснувши "Налаштувати".')}
                </p>
                <p className="cookie-privacy-link">
                  {t('cookies.learn_more_prefix', 'Дізнайтеся більше про те, як ми використовуємо ваші дані в нашій ')}
                  <button 
                    className="privacy-policy-link" 
                    onClick={openPrivacyPolicy}
                  >
                    {t('cookies.privacy_policy', 'Політиці конфіденційності')}
                  </button>.
                </p>
              </div>
              <div className="cookie-buttons">
                <button className="btn-reject" onClick={handleReject}>
                  {t('cookies.reject', 'Відхилити')}
                </button>
                <button className="btn-customize" onClick={handleCustomize}>
                  {t('cookies.customize', 'Налаштувати')}
                </button>
                <button className="btn-accept" onClick={handleAccept}>
                  {t('cookies.accept', 'Прийняти все')}
                </button>
              </div>
            </>
          ) : (
            // Налаштування згоди
            <>
              <div className="cookie-settings">
                <h3>{t('cookies.settings_title', 'Налаштування файлів cookie')}</h3>
                <p>{t('cookies.settings_description', 'Ви можете обрати, які типи файлів cookie ви дозволяєте використовувати на нашому сайті.')}</p>
                
                <div className="cookie-settings-options">
                  <div className="cookie-setting-item">
                    <div className="setting-header">
                      <h4>{t('cookies.essential_title', 'Необхідні')}</h4>
                      <div className="toggle disabled checked">
                        <div className="toggle-switch"></div>
                      </div>
                    </div>
                    <p>{t('cookies.essential_description', 'Ці файли cookie необхідні для функціонування сайту і не можуть бути вимкнені.')}</p>
                  </div>
                  
                  <div className="cookie-setting-item">
                    <div className="setting-header">
                      <h4>{t('cookies.analytics_title', 'Аналітичні')}</h4>
                      <div 
                        className={`toggle ${consentSettings.analytics ? 'checked' : ''}`}
                        onClick={() => handleSettingsChange('analytics')}
                      >
                        <div className="toggle-switch"></div>
                      </div>
                    </div>
                    <p>{t('cookies.analytics_description', 'Ці файли cookie допомагають нам розуміти, як відвідувачі взаємодіють з нашим сайтом, збираючи та повідомляючи інформацію анонімно.')}</p>
                  </div>
                  
                  <div className="cookie-setting-item">
                    <div className="setting-header">
                      <h4>{t('cookies.advertising_title', 'Рекламні')}</h4>
                      <div 
                        className={`toggle ${consentSettings.advertising ? 'checked' : ''}`}
                        onClick={() => handleSettingsChange('advertising')}
                      >
                        <div className="toggle-switch"></div>
                      </div>
                    </div>
                    <p>{t('cookies.advertising_description', 'Ці файли cookie використовуються для показу реклами, яка може бути більш релевантною для ваших інтересів.')}</p>
                  </div>
                </div>
                
                <div className="cookie-settings-buttons">
                  <button className="btn-reject" onClick={handleReject}>
                    {t('cookies.reject_all', 'Відхилити все')}
                  </button>
                  <button className="btn-accept" onClick={saveSettings}>
                    {t('cookies.save_settings', 'Зберегти налаштування')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      <PrivacyPolicyModal isOpen={showPrivacyPolicy} onClose={() => setShowPrivacyPolicy(false)} />
    </>
  );
};

export default CookieConsentBanner; 